import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import authService, { AuthError } from '../services/authService';
import Button from 'shared/components/UC/Button';
import styles from './LoginPage.module.css';

type FormMode = 'login' | 'forgot-password' | 'reset-sent';

const AdminLoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<
    string | null
  >(null);
  const [formMode, setFormMode] = useState<FormMode>('login');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for session expiration message from navigation state or sessionStorage
  useEffect(() => {
    const state = location.state as any;
    if (state?.sessionExpired) {
      setSessionExpiredMessage(
        state.message ||
          'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.'
      );
      // Clear the state to avoid showing the message on refresh
      window.history.replaceState({}, document.title);
    } else if (sessionStorage.getItem('session_expired') === 'true') {
      // Check sessionStorage for session expiration from file uploads (XHR)
      setSessionExpiredMessage(
        'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.'
      );
      sessionStorage.removeItem('session_expired');
    }
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsLoading(true);

    if (formMode === 'login') {
      try {
        await login({ email, password });
        // Redirect to home page after successful login
        navigate('/', { replace: true });
      } catch (error) {
        if (error instanceof AuthError) {
          setError(error.message);
        } else {
          setError('Error de inicio de sesión. Por favor, intenta nuevamente.');
        }
      } finally {
        setIsLoading(false);
      }
    } else if (formMode === 'forgot-password') {
      try {
        await authService.requestPasswordReset(email);
        setFormMode('reset-sent');
        setSuccessMessage(
          'Si el correo existe en nuestro sistema, se ha enviado un enlace de recuperación.'
        );
      } catch (error) {
        if (error instanceof AuthError) {
          if (error.statusCode === 429) {
            setError(
              'Demasiados intentos. Por favor, espera antes de intentar nuevamente.'
            );
          } else {
            // For security, we show success message even on errors
            setFormMode('reset-sent');
            setSuccessMessage(
              'Si el correo existe en nuestro sistema, se ha enviado un enlace de recuperación.'
            );
          }
        } else {
          setError('Error de conexión. Por favor, intenta nuevamente.');
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleModeChange = (mode: FormMode) => {
    setFormMode(mode);
    setError(null);
    setSuccessMessage(null);
    setPassword('');
    if (mode === 'login') {
      setEmail('');
    }
  };

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginColumnsContainer}>
        <div className={styles.leftColumn}>
          <div className={styles.valueProposition}>
            <div className={styles.titleContainer}>
              <h1 className={styles.valueTitle}>
                Concurso de Investigación: Avanza UC 2025
              </h1>
              <div
                className="uc-text-divider divider-secondary"
                style={{ width: '84px' }}
              ></div>
            </div>
            <div className={styles.valueContent}>
              <p>
                Como parte de sus primeras iniciativas, la Estrategia UC
                2026-2030 de la Pontificia Universidad Católica de Chile (UC) a
                través de la Dirección de Investigación (DINV) de la
                Vicerrectoría de Investigación y Postgrado (VRIP) invita a su
                cuerpo académico a postular al{' '}
                <b>Concurso de Investigación "Avanza UC 2025"</b>,
                destinado a impulsar proyectos audaces, de frontera, de alto
                riesgo e impacto, en las artes, humanidades, ciencias y
                tecnología, incluyendo desde investigaciones y creaciones
                fundamentales hasta sus formas más aplicadas, que busquen
                atender desafíos estratégicos de Chile y con potencial de
                trascender a una escala global.
              </p>
            </div>
          </div>
        </div>

        <div className={styles.rightColumn}>
          <div className={styles.loginCardContainer}>
            <div className={styles.loginCard}>
              <div className={styles.header}>
                <div className="d-flex align-items-center">
                  <h3>
                    {formMode === 'login' && 'Acceso Administrativo'}
                    {formMode === 'forgot-password' && 'Recuperar Contraseña'}
                    {formMode === 'reset-sent' && 'Correo Enviado'}
                  </h3>
                  <span className="uc-heading-decoration"></span>
                </div>
              </div>

              {sessionExpiredMessage && (
                <div className="uc-alert uc-alert-warning mb-3" role="alert">
                  <div className="d-flex align-items-start">
                    <i className="uc-icon me-2" style={{ fontSize: '1.25rem' }}>
                      warning
                    </i>
                    <div>
                      <strong>Sesión Expirada</strong>
                      <p className="mb-0">{sessionExpiredMessage}</p>
                    </div>
                  </div>
                </div>
              )}

              {error && <div className={styles.authError}>{error}</div>}

              {successMessage && (
                <div className={styles.successMessage}>{successMessage}</div>
              )}

              {formMode === 'reset-sent' ? (
                <div className={styles.resetSentContainer}>
                  <div className={styles.successIcon}>✓</div>
                  <p className={styles.resetSentText}>
                    Si el correo electrónico <strong>{email}</strong> está
                    registrado en nuestro sistema, recibirás un enlace para
                    restablecer tu contraseña.
                  </p>
                  <p className={styles.resetSentHelp}>
                    El enlace expirará en 24 horas. Si no recibes el correo,
                    revisa tu carpeta de spam.
                  </p>
                  <div className={styles.resetSentActions}>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => handleModeChange('login')}
                      style={{ marginRight: '0.5rem' }}
                    >
                      Volver al Login
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      onClick={() => handleModeChange('forgot-password')}
                    >
                      Enviar Otro Correo
                    </Button>
                  </div>
                </div>
              ) : (
                <form className={styles.form} onSubmit={handleSubmit}>
                  <div className={styles.formGroup}>
                    <label htmlFor="email" className={styles.label}>
                      Correo Electrónico
                    </label>
                    <input
                      id="email"
                      type="email"
                      className={styles.input}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isLoading}
                      placeholder={
                        formMode === 'login' ? 'admin@uc.cl' : 'tu.correo@uc.cl'
                      }
                    />
                  </div>

                  {formMode === 'login' && (
                    <div className={styles.formGroup}>
                      <label htmlFor="password" className={styles.label}>
                        Contraseña
                      </label>
                      <input
                        id="password"
                        type="password"
                        className={styles.input}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={isLoading}
                        placeholder="••••••••"
                      />
                    </div>
                  )}

                  <Button
                    type="submit"
                    variant="cta"
                    disabled={
                      isLoading || !email || (formMode === 'login' && !password)
                    }
                    style={{ width: '100%', justifyContent: 'center' }}
                  >
                    {isLoading && formMode === 'login' && 'Iniciando Sesión...'}
                    {!isLoading && formMode === 'login' && 'Iniciar Sesión'}
                    {isLoading &&
                      formMode === 'forgot-password' &&
                      'Enviando...'}
                    {!isLoading &&
                      formMode === 'forgot-password' &&
                      'Enviar Enlace de Recuperación'}
                  </Button>
                </form>
              )}

              {formMode === 'login' && (
                <div className={styles.forgotPassword}>
                  <button
                    type="button"
                    onClick={() => handleModeChange('forgot-password')}
                    className={styles.forgotPasswordLink}
                    disabled={isLoading}
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>
              )}

              {formMode === 'forgot-password' && (
                <div className={styles.forgotPassword}>
                  <button
                    type="button"
                    onClick={() => handleModeChange('login')}
                    className={styles.forgotPasswordLink}
                    disabled={isLoading}
                  >
                    Volver al login
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
